/**
 * Normalizzazione degli errori del BFF Limulo (`app-qlash-api`) in UN verdetto.
 *
 * ⚠️ IL FATTO CHE GIUSTIFICA QUESTO FILE: il BFF non ha un contratto d'errore, ne ha
 * QUATTRO, perche' una risposta puo' nascere in tre punti diversi della catena — il
 * middleware d'auth dell'host, il gate app/organizzazione dell'host, il router del BFF —
 * e nessuno dei tre conosce l'envelope degli altri. Misurati in vivo su
 * `staging-api.qlash.gg` il 2026-08-30 (i primi tre) e letti su `qlash-limulo@cabc619`:
 *
 *   host auth   401 → {"error":"No token provided"}                    ne' `ok` ne' `code`
 *   host org    400 → {"code":"E_ORGANIZATION_REQUIRED","error":…}     `code` ma non `ok`
 *   host gate   403 → {"error":"Required app is not active…",
 *                      "requiredApps":[…],"enabledApps":[…]}           `appAccess.ts:802`
 *   BFF        4xx → {"ok":false,"error":…,"code":"E_QLASH_*",
 *                      "correlationId":…}                              `public-errors.js`
 *
 * Chi discrimina su `code` — la cosa che verrebbe naturale — indovina in un caso su
 * quattro. Da qui l'unico punto del client che sa leggere tutte e quattro le forme.
 *
 * ⚠️ E IL LIMITE, dichiarato invece che nascosto: `sanitizePublicDetails`
 * (`public-errors.js:52-98` @ `7e4d86e`) ha una whitelist e butta via ogni altro dettaglio
 * prima che esca. Quindi un 403 nato DENTRO il BFF **senza un `code` suo** arriva al client
 * NUDO, e resta `forbidden`: `forbidden` non degrada in «non ancora servito da Limulo», perche'
 * dire all'utente una cosa che non sappiamo e' peggio che dirgli che non lo sappiamo.
 *
 * 📌 **L'interruttore d'organizzazione NON e' un 403 nudo, e questo file l'ha letto tardi.**
 * Le righe che stavano qui (rimisura del 2026-09-06 su `46404fc`) dicevano che
 * `assertEndpointEnabled` costruisce l'errore **senza** `isPublicError`, quindi 500 travestito
 * e dettagli buttati. **Era gia' falso allora**: da `qlash-limulo@17d7ab7` (2026-08-31,
 * antenato di `46404fc`) la funzione lancia `createPublicHttpError(403, 'Endpoint Qlash API
 * disabilitato per il tenant', {endpointId, settingsKey}, E_QLASH_ENDPOINT_DISABLED)` —
 * `policy.js:139-155` @ `origin/staging` = `7e4d86e` — e le due chiavi stanno nella whitelist
 * (`PUBLIC_DETAIL_KEYS`, `public-errors.js:30-38`). Misurato dal vivo il 2026-09-23 (kb#253):
 * `/v1/games/backgammon/*` → `403 E_QLASH_ENDPOINT_DISABLED`, `settingsKey: enableBackgammon`.
 * ⇒ Quel 403 ha il verdetto suo, `switch-off` (v. `VERDICT_BY_CODE`). Fino al 2026-09-26
 * cadeva in `forbidden`, e la chat diceva «possono scriverci solo i giocatori dell'incontro e
 * lo staff» a chi aveva soltanto `enableChat` spento sull'organizzazione.
 * Il valore va riletto alla fonte prima di contarci: e' un file di un altro repo, cambia senza
 * lasciare qui nessun segnale.
 */

export type LimuloVerdict =
  /** 401 dell'host: token assente, malformato o scaduto. Si rifa' l'auth. */
  | 'unauthenticated'
  /**
   * 🔴 **NOI abbiamo rinunciato ad aspettare.** Non e' un rifiuto di nessuno: la richiesta
   * e' stata abbandonata dalla nostra scadenza (`fetchWithTimeout`), e il token era valido.
   *
   * Aggiunto il 2026-09-06 dopo un difetto che avevamo introdotto UN'ORA prima con #178:
   * la scadenza sulla catena di sessione mordeva (45 s → 18 s, bene), ma l'utente leggeva
   * «Identita' non valida — Serve una nuova autenticazione». Cioe' gli dicevamo di **rifare
   * il login** — dentro Telegram, col ponte verso il browser di sistema — quando gli
   * bastava **riprovare**.
   * 📌 *E' il costo piu' alto che una copy sbagliata possa avere in questa app, ed era
   * nostro: le altre bugie di stasera le abbiamo trovate, questa l'abbiamo fatta.*
   *
   * ⛔ Non si riusa `network`, e la ragione e' nella frase: `degraded.network` dice
   * «Controlla la connessione», che e' vero per una rete caduta e **discutibile per una
   * scadenza che abbiamo scelto noi a 15 s** — la connessione dell'utente puo' essere
   * perfetta. La differenza da dire e' «ci ho rinunciato io», non «non sei tu».
   */
  | 'timeout'
  /** 403 dell'host: l'app `qlash-api` non e' attiva sull'organizzazione. */
  | 'app-off'
  /** 403 dell'host: la capability richiesta non e' abilitata sull'organizzazione. */
  | 'capability-off'
  /** 403 dell'host: l'organizzazione selezionata non combacia col membership del token. */
  | 'org-mismatch'
  /** 400 dell'host: manca del tutto un'organizzazione autenticata. */
  | 'org-required'
  /**
   * 424 del BFF. ⚠️ NON significa solo «nessun provider»: `router.js:210-232` fa uscire
   * con lo stesso status e lo stesso messaggio anche il torneo INESISTENTE e il filtro
   * invalido. Il motivo vero resta in `internalDetails`, che al client non arriva mai.
   * Il nome del verdetto dice quel dubbio invece di nasconderlo.
   */
  | 'domain-or-missing'
  /** 403 che non porta indizi: NON si indovina quale dei tre significati sia. */
  | 'forbidden'
  /**
   * 403 del BFF con `code: E_QLASH_ENDPOINT_DISABLED` (`policy.js:139-155` @ `7e4d86e`): un
   * interruttore dell'organizzazione (`enableChat`, `enableBackgammon`, …) spegne la famiglia di
   * rotte. E' CONFIGURAZIONE — non un permesso che manca all'utente, non un guasto — e il BFF lo
   * controlla PRIMA di scendere alla capability (`createRuntimeContext`, `router.js:197-214`):
   * niente e' stato scritto. La frase dice «questa funzione non e' attiva qui» e non promette
   * che riprovare serva.
   * ⛔ `details.settingsKey` dice QUALE interruttore, e all'utente NON si mostra: e' materia di
   * chi gestisce l'organizzazione. Chi indaga lo trova nel log del BFF (`sendPublicError` scrive
   * il `payload` accanto al `correlationId`, che la schermata gia' mostra).
   */
  | 'switch-off'
  /** 401 del BFF: il token ESTERNO e' stato rifiutato. Nessun refresh puo' curarlo. */
  | 'external-token-invalid'
  /** 502 del BFF: l'issuer OIDC non risponde. Non e' un guasto nostro. */
  | 'identity-provider-down'
  /** 403 del BFF: l'issuer non dichiara verificata l'email dell'utente. */
  | 'email-not-verified'
  /**
   * 500 con `E_QLASH_INTERNAL`: guasto vero **oppure** interruttore d'org spento. ⚠️ L'interruttore
   * per-rotta non arriva piu' cosi' (ha `switch-off`): v. la nota sopra `verdictFor`.
   */
  | 'internal-or-switch-off'
  | 'bad-request'
  | 'not-found'
  /**
   * 409. ⚠️ **Oggi non puo' arrivare, ed e' un ramo di CONTRATTO, non un'osservazione.**
   * Il dominio del provider dichiara `http: 409` su «qualcuno ha gia' refertato un punteggio
   * diverso» (`qTournaments core/risultati.ts:191-198`), «check-in su incontro concluso»
   * (`:216-217`) e «prove senza referto» (`:222-223`) — ma il runtime dell'host appiattisce
   * ogni rifiuto del provider in una stringa e risponde 424 (v. `matchActions.ts`, cappello).
   * Il ramo resta perche' il giorno in cui l'host smette di appiattire, un 409 che cade in
   * `unknown` direbbe «esito non riconosciuto» su un messaggio perfettamente chiaro.
   */
  | 'conflict'
  /** 413: file oltre il limite. Il BFF lo lancia DA SOLO, con `maxBytes`/`size` nei dettagli. */
  | 'payload-too-large'
  /** 415: tipo file non ammesso. Stessa origine del 413, con `mimeType`/`allowedMimeTypes`. */
  | 'unsupported-media'
  | 'server'
  /** La richiesta non e' mai arrivata: rete caduta o timeout. */
  | 'network'
  /**
   * 422: il server ha capito la richiesta e ha RIFIUTATO i dati — dal 2026-09-23 e' la forma dei
   * rifiuti di validazione del provider promossi dal bordo (profilo di gioco: token di verifica
   * non valido o gia' usato, tag gia' collegato, …; iscrizione: i motivi di kb#237).
   * Prima cadeva in `unknown` («esito non riconosciuto»). ⚠️ Il MOTIVO specifico non sta nello
   * status: dove il bordo lo nomina (iscrizione) lo legge `registrationReasonOf`.
   * 📌 Il caso che ha aperto questa correzione NON era un 422: v. `bad-request` in `gating.ts`.
   */
  | 'unprocessable'
  | 'unknown';

export interface LimuloFailure {
  verdict: LimuloVerdict;
  /** Lo status HTTP quando c'e' stata una risposta; `null` se la rete non e' arrivata. */
  status: number | null;
  /** `E_QLASH_*` o `E_ORGANIZATION_REQUIRED`; `null` per gli envelope dell'host che non ne hanno. */
  code: string | null;
  /** Il messaggio del server, mai inventato: stringa vuota se non ne ha mandato uno. */
  message: string;
  /** L'unica cosa che permette a Fabio di ritrovare la richiesta nei log del BFF. */
  correlationId: string | null;
  /** Popolato solo dal 403 dell'host: quali app mancano. Mai `undefined`. */
  requiredApps: string[];
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/**
 * Il 403 «organizzazione che non combacia» e' l'unico dei tre che non porta un campo
 * strutturato: si riconosce solo dal messaggio (`organization-context.ts:66-70`). E' un
 * riconoscimento fragile per costruzione — se il messaggio cambia lato server, il caso
 * ricade su `forbidden`, che e' il fallback giusto: si perde precisione, non correttezza.
 */
const ORG_MISMATCH_MARKER = 'does not match the active membership';
/**
 * La QUINTA busta, trovata il 2026-09-05 addosso a un utente vero: il middleware d'auth
 * dell'host (`middlewares/auth.ts:282`) risponde `400 {error: "X-AM-ORGANIZATION header
 * is required"}` — **senza `code` e senza `ok`**, quindi nessuno dei rami basati sul
 * codice la intercettava e finiva in `bad-request`, cioe' nella copy «esito non
 * riconosciuto».
 *
 * Quella schermata ha fatto la cosa giusta — ha riportato il messaggio grezzo invece di
 * inventare — ma un verdetto che esiste gia' (`org-required`) dice la stessa cosa in modo
 * utilizzabile. Si riconosce dal MESSAGGIO perche' non c'e' altro da leggere.
 */
const HOST_ORG_REQUIRED_MARKER = 'X-AM-ORGANIZATION header is required';

/**
 * I tre 403 che il BFF genera DA SOLO, dentro l'handler d'auth
 * (`mobile-auth.js:534-557`, chiamata alla riga 822 di `exchangeMobileToken`): il gate
 * dell'app e' riscritto a mano li' dentro invece che montato come middleware, quindi
 * l'envelope e' quello del BFF e `requiredApps` NON c'e' — `sanitizePublicDetails` lo
 * avrebbe comunque buttato via. Resta il testo del messaggio, in italiano.
 *
 * Riconoscere un errore per stringa e' fragile, e per questo il fallback e' `forbidden`:
 * se il messaggio cambia lato server si perde precisione, non correttezza. Nessuno di
 * questi marcatori inventa un verdetto piu' preciso di quello che il server ha detto.
 */
const BFF_APP_OFF_MARKER = 'non attiva per il tenant';
const BFF_CAPABILITY_OFF_MARKER = 'Capability Qlash API non disponibile';

/**
 * Tre errori dell'auth che il BFF marca con un `code` esplicito
 * (`public-errors.js:5-11`, lanciati in `mobile-auth.js:172/179/323`). Si leggono dal
 * codice, non dal messaggio: dove il codice c'e', usarlo e' obbligatorio — leggere una
 * stringa in italiano avendo a disposizione un codice sarebbe una scelta, non una
 * necessita' come per i 403 del gate app.
 *
 * Meritano un verdetto proprio perche' portano ad azioni diverse: un token esterno
 * rifiutato non si cura rinfrescando (il materiale in ingresso e' sbagliato: issuer,
 * audience, algoritmo o client_id), un issuer giu' non e' colpa nostra, e un'email non
 * verificata e' una cosa che l'utente puo' sistemare.
 */
const VERDICT_BY_CODE: Record<string, LimuloVerdict> = {
  E_QLASH_EXTERNAL_TOKEN_INVALID: 'external-token-invalid',
  E_QLASH_IDENTITY_PROVIDER_UNAVAILABLE: 'identity-provider-down',
  E_QLASH_EMAIL_NOT_VERIFIED: 'email-not-verified',
  E_QLASH_PROVIDER_UNAVAILABLE: 'domain-or-missing',
  // L'interruttore dell'organizzazione spento (v. `switch-off`). Si legge dal CODICE, come le
  // righe sopra: il messaggio («Endpoint Qlash API disabilitato per il tenant») e' in italiano e
  // puo' cambiare. ⛔ Senza questo codice un 403 resta `forbidden`, con la sua copy di sempre.
  E_QLASH_ENDPOINT_DISABLED: 'switch-off',
};

/**
 * ⚠️ UN 500 CON `E_QLASH_INTERNAL` NON E' LA PROVA DI UN BUG DEL SERVER — ma l'interruttore
 * per-rotta, che era la ragione di questa nota, NON ARRIVA PIU' COSI'.
 *
 * La nota diceva: `assertEndpointEnabled` costruisce un `Error` con `statusCode = 403` ma
 * **senza `isPublicError`**; `sendPublicError` onora lo status solo con quel flag, altrimenti
 * forza 500 + `E_QLASH_INTERNAL` + `details: undefined` ⇒ l'interruttore spento arriva
 * travestito da guasto. **Vero sul BFF letto il 2026-08-30 (`cabc619`, `policy.js:97-111`);
 * superato da `qlash-limulo@17d7ab7` (2026-08-31).** Oggi (`policy.js:139-155` @ `7e4d86e`)
 * l'interruttore esce come `403` con `code: E_QLASH_ENDPOINT_DISABLED` e
 * `details: {endpointId, settingsKey}`, e ha il verdetto `switch-off`. E' esattamente il fix
 * minimo chiesto a Fabio su kb#91 («`createPublicHttpError` + un codice dedicato»). La
 * rimisura del 2026-09-06 che correggeva questa nota (whitelist a sette chiavi, `46404fc`)
 * aveva davanti gia' il BFF corretto, e non l'ha visto.
 *
 * ⚠️ Perche' `internal-or-switch-off` resta com'e': un 500 `E_QLASH_INTERNAL` e' ancora OGNI
 * errore non pubblico del BFF, e nessuno ha misurato che nessun altro interruttore passi di li'
 * — ne' quale BFF serva ogni ambiente. Togliergli la seconda lettura e' una decisione a se', non
 * un effetto collaterale di `switch-off`. Un 500 senza quel codice resta `server`: li' non
 * abbiamo nessun motivo di dubitare che sia un guasto.
 */

const verdictFor = (
  status: number,
  body: Record<string, unknown>,
  message: string,
  code: string | null,
  requiredApps: string[],
): LimuloVerdict => {
  if (code && VERDICT_BY_CODE[code]) return VERDICT_BY_CODE[code];
  if (status === 401) return 'unauthenticated';
  if (status === 403) {
    if (requiredApps.length > 0) return 'app-off';
    if (asStringArray(body.requiredCapabilities).length > 0) return 'capability-off';
    if (message.includes(BFF_CAPABILITY_OFF_MARKER)) return 'capability-off';
    if (message.includes(BFF_APP_OFF_MARKER)) return 'app-off';
    if (message.includes(ORG_MISMATCH_MARKER)) return 'org-mismatch';
    // Fra i 403 nudi finisce anche `enableAuthExchange === false` («Exchange token
    // Qlash API disabilitato per il tenant»): e' un interruttore del tenant, NON l'app
    // spenta. Chiamarlo `app-off` manderebbe qualcuno ad accendere una cosa gia' accesa.
    // ⚠️ Oggi (`mobile-auth.js:787-794` @ `7e4d86e`) porta un codice suo,
    // `E_QLASH_AUTH_EXCHANGE_DISABLED`, che qui NON si mappa di proposito: e' l'exchange, non
    // una rotta della sezione, e `switch-off` e' stato aggiunto per `E_QLASH_ENDPOINT_DISABLED`
    // e basta. Resta `forbidden`, come prima.
    return 'forbidden';
  }
  if (status === 400) {
    if (code === 'E_ORGANIZATION_REQUIRED') return 'org-required';
    if (message.includes(HOST_ORG_REQUIRED_MARKER)) return 'org-required';
    return 'bad-request';
  }
  if (status === 404) return 'not-found';
  if (status === 409) return 'conflict';
  // ⚠️ 413 e 415 sono l'ECCEZIONE alla regola del cappello: NON nascono nel provider e non
  // passano dall'appiattimento del runtime. Li lancia il BFF stesso, prima di scendere alla
  // capability (`validateUploadedFile`, `router.js:378-398`), con `isPublicError` e dettagli
  // che stanno nella whitelist — quindi arrivano NOMINATI e utilizzabili. Sono, oggi, gli
  // unici due rifiuti di scrittura su cui si puo' dire all'utente cosa cambiare.
  if (status === 413) return 'payload-too-large';
  if (status === 415) return 'unsupported-media';
  if (status === 422) return 'unprocessable';
  if (status === 424) return 'domain-or-missing';
  if (status >= 500) return code === 'E_QLASH_INTERNAL' ? 'internal-or-switch-off' : 'server';
  return 'unknown';
};

/**
 * Traduce un errore di RTK Query (`{status, data}` per una risposta, `{status:'FETCH_ERROR'}`
 * per una richiesta mai arrivata) nel verdetto unico. Non lancia mai: riceve per definizione
 * cio' che la rete ha prodotto, e una schermata non deve esplodere perche' davanti al BFF
 * ha risposto un nginx con dell'HTML.
 */
export function limuloFailure(error: unknown): LimuloFailure {
  const envelope = asRecord(error);
  const rawStatus = envelope.status;
  const body = asRecord(envelope.data);

  const message = asString(body.error);
  const code = asString(body.code) || null;
  const correlationId = asString(body.correlationId) || null;
  const requiredApps = asStringArray(body.requiredApps);

  if (typeof rawStatus !== 'number') {
    return {
      // ⚠️ I due non sono piu' la stessa cosa, e la differenza e' cio' che l'utente puo'
      // farci: `FETCH_ERROR` e' la rete che e' caduta (controlla la connessione),
      // `TIMEOUT_ERROR` siamo NOI che abbiamo smesso di aspettare (riprova).
      verdict:
        rawStatus === 'TIMEOUT_ERROR'
          ? 'timeout'
          : rawStatus === 'FETCH_ERROR'
            ? 'network'
            : 'unknown',
      status: null,
      code,
      message,
      correlationId,
      requiredApps,
    };
  }

  return {
    verdict: verdictFor(rawStatus, body, message, code, requiredApps),
    status: rawStatus,
    code,
    message,
    correlationId,
    requiredApps,
  };
}

/**
 * I motivi (dieci alla nascita, dodici col consenso al regolamento, tredici coi premi non verificabili, quattordici col profilo non verificabile) con cui il BFF (Limulo #68, `registration-refusal.js`) accompagna il rifiuto di
 * `POST /v1/tournaments/:id/registration` in `details.registrationReason`. Vocabolario CHIUSO:
 * lo rispecchia anche il serializzatore pubblico del BFF, quindi un settimo valore non esiste
 * finche' chi possiede il BFF non lo aggiunge — e qui cade nel ramo «sconosciuto», non in
 * un'invenzione.
 *
 * `party.member-already-left` e' il sesto: proiettato dal BFF dal 21/09 15:01Z (kb#187,
 * @phomea, staging). ⚠️ Il motore lo emette da QUATTRO scritture diverse
 * (`persistenza/party.model.ts` :1104 · :1484 · :1611 · :1785) e solo una porta `party`:
 * il codice NON identifica la causa, quindi nemmeno la copy puo' affermarla.
 */
export const REGISTRATION_REASONS = [
  'window.not-published',
  'window.before-open',
  'window.after-close',
  'window.state-closed',
  'window.capacity-reached',
  'party.member-already-left',
  // kb#237 — i quattro rifiuti del PROFILO DI GIOCO, proiettati dal bordo con `qlash-limulo#118`
  // (`registration-refusal.js`, testa `0cea4e15`, @gionko 2026-09-23). ⚠️ Arrivano col **422**,
  // il default della famiglia `iscrizione` nel motore (`superficie/rotte/contratto.js`,
  // `iscrizione: 422`, nessuna eccezione per questi codici), NON col 409 dei motivi di finestra.
  'profile.required',
  'profile.rejected',
  'profile.user-mismatch',
  'identity.unresolved',
  // qTournaments 1.77.0 (mirror qlash-limulo#158): un profilo dichiarato si CONFERMA col profilo
  // attivo che il gioco ha per chi entra, e un'identita' che il gioco non ha e'
  // `iscrizione.profile-identity-mismatch` (422, stessa famiglia). Il bordo lo proietta col SOLO
  // codice — mai `dichiarato`/`delGioco`, che possono essere di un compagno o del giocatore iscritto
  // dallo staff (@gionko su #158, issuecomment-5857024278). Nome fissato da @phomea in qlash-limulo#181
  // (`registration-refusal.js`), servito su staging da `staging-f0119c7` (01/10 17:57Z).
  'profile.identity-mismatch',
  // Il CONSENSO al regolamento (qTournaments 1.93.0). ⚠️ Presi da `qlash-limulo#181` (@eec2276), APERTA e
  // NON servita: il BFF di oggi non li porta (il 422 arriva senza `details.registrationReason`) e il nome
  // puo' ancora cambiare prima della fusione [T limulo-core-orcr 14:26Z, via tma-orcr]. Se cambia sono due
  // stringhe qui e due chiavi in `REGISTRATION_REASON_COPY`. `rules.consent-required` arriva col 422,
  // `rules.changed` col 409.
  'rules.consent-required',
  'rules.changed',
  // I PREMI NON VERIFICABILI (qTournaments 1.99.1, `qlash-limulo#196`, APERTA): se all'iscrizione QBank non
  // risponde, il motore rifiuta con `iscrizione.premi-non-verificabili`. Nome pubblico scelto da @gionko
  // (`qlash-limulo#196`, commento 5969377084), proiezione in `qlash-limulo#197` (`registration-refusal.js`,
  // gemella di qTournaments#162): arriva sul servito solo quando entrano ENTRAMBE, oggi su staging non nasce.
  // 🔴 E arriva col **424**, NON col 409/422 di tutti gli altri: e' un guasto PASSEGGERO («riprova»), non un
  // rifiuto del dominio — v. `MOTIVI_SOLO_SU_424` e `registrationReasonOf`.
  'registration.prizes-unverifiable',
  // Il PROFILO NON VERIFICABILE (qTournaments `iscrizione.profilo-non-verificabile`, 424, ADR-0187 §9; `qlash-limulo#207`,
  // fusa in staging il 2026-10-05 08:22:58Z, @gionko): il gioco non risponde sul profilo. Il nome pubblico `profile.unverifiable`
  // e' in `registration-refusal.js:32,120-121` @ `qlash-limulo@origin/staging` = `ae0ddb2` [M 2026-10-05].
  // Il 424 vale, dalla qT 1.114.0 (staging `ae0ddb2`, limulo#209, 05/10), per il profilo DICHIARATO (dalla 1.95.0) **e** per una
  // parte di GRUPPO il cui gioco tace sui profili dei membri (kb#219): `iscrizioni.js:2002-2005` (gruppo) e `:2053-2058` (dichiarato)
  // @ `ae0ddb2` [M], `PUBLIC_API_CONTRACT.md:168-170` [M]. Fino alla 1.113.0 il gruppo prendeva 201. La frase NON dice di CHI
  // e' il profilo (col gruppo puo' essere di un compagno).
  // 🔴 Come i premi: arriva col **424**, e significa «riprova», NON «profilo sbagliato» (quello e' `profile.rejected` /
  // `profile.identity-mismatch`, 422) — v. `MOTIVI_SOLO_SU_424` e `registrationReasonOf`.
  'profile.unverifiable',
] as const;

export type LimuloRegistrationReason = (typeof REGISTRATION_REASONS)[number];

/**
 * I motivi che nascono col **424** e valgono SOLO con quello. Il cancello di status di
 * `registrationReasonOf` e' per MOTIVO, non per famiglia: un 424 con un altro motivo del vocabolario NON passa
 * (un 424 e' di norma «nessun provider», `domain-or-missing`: aprirlo a tutti i motivi leggerebbe un guasto di
 * infrastruttura come un rifiuto nominato), e questi motivi con un 409/422 neppure (non e' il loro status: il
 * bordo non li manda cosi', e un valore con lo status sbagliato e' un'altra cosa da quella documentata).
 */
const MOTIVI_SOLO_SU_424: ReadonlySet<LimuloRegistrationReason> = new Set<LimuloRegistrationReason>([
  'registration.prizes-unverifiable',
  'profile.unverifiable',
]);

/**
 * Il motivo del rifiuto d'iscrizione, o `null`. Legge SOLO `data.details.registrationReason` e
 * solo su uno status **409 o 422** — **424 per i soli `MOTIVI_SOLO_SU_424`**: mai dal messaggio, mai da altri
 * status. Un valore fuori vocabolario, un tipo sbagliato o l'assenza del campo tornano `null` — e il chiamante
 * cade sul messaggio di prima (per un 424 senza motivo: `domain-or-missing`, come oggi).
 *
 * 🔴 Fino al 2026-09-23 il cancello era `status !== 409`: un cancello sullo STATO, e i rifiuti
 * del profilo (kb#237) sono **422** ⇒ anche col bordo che li nomina, la TMA li avrebbe buttati.
 * Era uno dei cinque anelli di kb#237 che, corretto da solo, non cambia niente a schermo.
 */
export function registrationReasonOf(error: unknown): LimuloRegistrationReason | null {
  const envelope = asRecord(error);
  if (envelope.status !== 409 && envelope.status !== 422 && envelope.status !== 424) return null;
  const reason = asRecord(asRecord(envelope.data).details).registrationReason;
  const known = REGISTRATION_REASONS.find((candidate) => candidate === reason) ?? null;
  if (known === null) return null;
  // Il 424 e' di certi motivi e quei motivi sono del 424: o si e' nell'uno o si e' nell'altro.
  return MOTIVI_SOLO_SU_424.has(known) === (envelope.status === 424) ? known : null;
}

/**
 * I QUATTRO motivi con cui il bordo accompagna il rifiuto del check-in e del referto di un
 * INCONTRO in `details.matchReason` (qTournaments 1.69.0, ADR-0158): `MATCH_REASONS` di
 * `match-refusal.js` @ `qlash-limulo#134` (testa `a467d11`), vocabolario chiuso che il
 * serializzatore pubblico ricontrolla. ⚠️ Copia dell'allowlist del server, come
 * `REGISTRATION_REASONS`: ogni motivo ha una frase sua, e un quinto valore non ha frase.
 *
 *  · `checkin.expired` — il tempo per rispondere e' finito: DEFINITIVO, non si riprova;
 *  · `checkin.already-present` — «non sono pronto» da chi ha gia' fatto il check-in: per
 *    rinunciare adesso c'e' il ritiro;
 *  · `checkin.match-settled` — l'incontro e' gia' deciso;
 *  · `match.awaiting-check-in` — referto prima dei due check-in: si aspetta fino a
 *    `matchCheckIn.deadlineAt`.
 * ⛔ `checkin.attivo-altrove` non e' qui e non va aggiunto: era un motivo del check-in AL TORNEO,
 * e da qTournaments 1.69.0 non esiste piu' nemmeno li'.
 */
export const MATCH_REASONS = [
  'checkin.expired',
  'checkin.already-present',
  'checkin.match-settled',
  'match.awaiting-check-in',
] as const;

export type LimuloMatchReason = (typeof MATCH_REASONS)[number];

/**
 * Il motivo del rifiuto di check-in o referto di un incontro, o `null`. Legge SOLO
 * `data.details.matchReason`, mai il messaggio; un valore fuori vocabolario, un tipo sbagliato
 * o l'assenza del campo tornano `null` — e il chiamante fa quello che faceva prima: RILEGGE.
 *
 * ⚠️ Il cancello sullo stato ammette **409 e 422**, non solo il 409 che il contratto dichiara per
 * tutti e quattro: e' la lezione di `registrationReasonOf` (kb#237), dove un cancello sul solo 409
 * buttava i motivi che il motore promuoveva col 422 della sua famiglia. Il vocabolario chiuso resta
 * la guardia vera; lo stato serve solo a non leggere un motivo dentro una risposta che non e' un
 * rifiuto di dominio.
 */
export function matchReasonOf(error: unknown): LimuloMatchReason | null {
  const envelope = asRecord(error);
  if (envelope.status !== 409 && envelope.status !== 422) return null;
  const reason = asRecord(asRecord(envelope.data).details).matchReason;
  return MATCH_REASONS.find((known) => known === reason) ?? null;
}

/**
 * I VENTUNO motivi con cui il BFF accompagna il rifiuto delle rotte `/v1/challenges` in
 * `details.challengeReason` (contratto PR169, letto nel codice — [C], NON misurato — 28/09,
 * riverificato dopo la fusione: la PR è fusa 18:21:29Z, testa `f0db426`, il pezzo qui sotto e'
 * invariato rispetto a `b690928`). Vocabolario CHIUSO come `REGISTRATION_REASONS`.
 * Il ventunesimo, `sfida.abbinato-in-coda` (emesso SOLO su accept, 409), viene da qlash-limulo `origin/staging`
 * `cf895808`, letto dal sorgente da un sub-agente del seggio il 30/09 — [C], NON visto sul vivo.
 *
 * ⚠️ **Qui NON si legge lo status prima del motivo**, a differenza di `registrationReasonOf` e
 * `matchReasonOf`. Il contratto della sfida dichiara `code:'E_QLASH_HTTP_<status>'` — cioe' lo
 * status e' gia' dentro il CODICE, non un valore fisso da controllare — e nessuna misura dice su
 * quali status arrivino i ventuno motivi (per `sfida.abbinato-in-coda` lo status e' scritto nel motore: 409,
 * `superficie/rotte/contratto.js:536` @ cf895808, letto dal sorgente, non visto sul vivo). Filtrare per status qui
 * inventerebbe un cancello che il contratto non promette; il vocabolario chiuso resta la guardia vera, come per gli altri due.
 */
export const CHALLENGE_REASONS = [
  'sfida.abbinato-in-coda',
  'sfida.amicizia-non-verificabile',
  'sfida.campo-illeggibile',
  'sfida.conflitto',
  'sfida.contro-se-stessi',
  'sfida.destinatario-ambiguo',
  'sfida.destinatario-sconosciuto',
  'sfida.gioco-muto',
  'sfida.gioco-rifiutato',
  'sfida.non-amico',
  'sfida.non-destinatario',
  'sfida.non-in-attesa',
  'sfida.non-trovata',
  'sfida.profilo-respinto',
  'sfida.profilo-richiesto',
  'sfida.profilo-rifiutato',
  'sfida.propria',
  'sfida.scaduta',
  'sfida.solo-chi-sfida',
  'sfida.troppe-in-attesa',
  'sfida.validazione-rotta',
] as const;

export type LimuloChallengeReason = (typeof CHALLENGE_REASONS)[number];

/**
 * Il motivo del rifiuto di una rotta `/v1/challenges`, o `null`. Legge SOLO
 * `data.details.challengeReason`: un valore fuori vocabolario, un tipo sbagliato o l'assenza del
 * campo tornano `null`, e il chiamante cade sul messaggio generico col codice in chiaro (dispatch
 * sfide-orcr-3-invio, punto 2).
 */
export function challengeReasonOf(error: unknown): LimuloChallengeReason | null {
  const envelope = asRecord(error);
  const reason = asRecord(asRecord(envelope.data).details).challengeReason;
  return CHALLENGE_REASONS.find((known) => known === reason) ?? null;
}

/**
 * Lo status con cui il sync del profilo dice «identita' assente o ambigua» (kb#388): ritentare non serve. Lo legge il verdetto del
 * sync (`verdettoDelSync`, `registration.ts`): un posto solo, perche' due letterali divergerebbero.
 */
export const STATUS_SYNC_IDENTITA = 403;

/**
 * Il vocabolario CHIUSO di `details.profileReason` (BFF `profile-refusal.js`, Limulo origin/staging 0dd9467, kb#388): sei
 * valori. Un valore che non conosciamo non e' un errore e non e' una frase: e' `null`, e il chiamante cade sulla frase di
 * sempre.
 */
export const PROFILE_REASONS = [
  'profile.token-required',
  'profile.token-invalid',
  'profile.user-invalid',
  'profile.tag-invalid',
  'profile.provider-unavailable',
  'profile.not-linked',
] as const;

export type LimuloProfileReason = (typeof PROFILE_REASONS)[number];

/**
 * Il motivo-profilo di un rifiuto, o `null`. UN posto solo, accanto a `challengeReasonOf`: legge SOLO
 * `data.details.profileReason`, e un valore fuori vocabolario, un tipo sbagliato o l'assenza del campo tornano `null`.
 * ⚠️ Per la Sfida conta solo `profile.not-linked` (`esitoProfiloNonCollegato`, `challenge.ts`): gli altri cinque cadono
 * sulla frase di oggi.
 */
export function profileReasonOf(error: unknown): LimuloProfileReason | null {
  const envelope = asRecord(error);
  const reason = asRecord(asRecord(envelope.data).details).profileReason;
  return PROFILE_REASONS.find((known) => known === reason) ?? null;
}

/**
 * ⚠️⚠️ NESSUN CONSUMATORE OGGI — E NON E' «MORTA», E' DIVERGENTE. NON RICOLLEGARLA
 * SENZA DECIDERE PRIMA.
 *
 * Nessun file di `src/` chiama questa funzione (controllo del 2026-09-02). Le schermate
 * passano tutte da `degradedCopyKeys` in `gating.ts`, che e' la fonte VIVA — e le due
 * rispondono alla stessa domanda («questi verdetti vanno trattati insieme?») dando
 * risposte ormai DIVERSE:
 *
 *   qui                     → domain-or-missing · app-off · capability-off  (insieme)
 *   degradedCopyKey (viva)  → domain-or-missing da solo;
 *                             app-off · capability-off insieme
 *
 * La divergenza e' nata con la copy corta decisa da Luca il 2026-08-31 e nessuno l'ha
 * vista, perche' il gate e' verde su questa funzione mentre a schermo gira l'altra.
 * ⇒ Ricollegarla in buona fede **ripristinerebbe un comportamento diverso da quello
 * attuale** senza che nulla lo segnali. Se serve di nuovo, si decide prima QUALE delle
 * due raggruppature e' quella giusta, e si tiene una fonte sola.
 *
 * (Da decidere in fase 3, quando la catena viva dira' se la distinzione serve davvero.
 * Vedi anche la nota gemella su `isAuthFailure`.)
 *
 * ── La sua descrizione originale, che resta valida sul proprio terreno ──
 * «Limulo questa cosa oggi non la serve» — la sola domanda che le schermate fanno per
 * decidere se mostrare il degrado onesto invece di una lista vuota.
 *
 * La lista e' corta di proposito. Un 401 NON e' qui dentro: la trappola documentata nella
 * mappa BFF (l'auth gira PRIMA del gate app, `appAccess.ts:769-820`) fa comparire 401 dove
 * il problema vero e' un 403, e mostrare «non ancora servito» a chi ha solo il token scaduto
 * lo manda a cercare il guasto dove non e'.
 */
export function isNotServed(failure: LimuloFailure): boolean {
  return (
    failure.verdict === 'domain-or-missing' ||
    failure.verdict === 'app-off' ||
    failure.verdict === 'capability-off'
  );
}

/**
 * ⚠️ NESSUN CONSUMATORE OGGI (controllo del 2026-09-02). A differenza di `isNotServed`
 * qui NON c'e' divergenza: nessun'altra funzione risponde alla stessa domanda, quindi
 * ricollegarla non cambierebbe un comportamento esistente — semplicemente oggi nessuno
 * la chiede. La distinzione che nomina servira' in fase 3 (rifare l'exchange contro
 * rimandare al login), ed e' li' che si decide se collegarla o toglierla.
 *
 * ── Descrizione ──
 * L'identita' non regge. Due modi molto diversi, entrambi qui dentro perche' entrambi
 * fermano la sezione: la NOSTRA sessione non vale piu' (`unauthenticated` — si rifa'
 * l'exchange), oppure il token ESTERNO e' stato rifiutato (`external-token-invalid` —
 * rifare l'exchange con lo stesso materiale produrrebbe lo stesso 401).
 */
export function isAuthFailure(failure: LimuloFailure): boolean {
  return failure.verdict === 'unauthenticated' || failure.verdict === 'external-token-invalid';
}


/**
 * Un 401 puo' essere causato da NOI, e in quel caso non e' un problema di identita'.
 *
 * 🔴 Il percorso misurato il 2026-09-06 (or45), che nessuna delle due meta' spiegava da sola:
 * l'exchange di sessione scade ⇒ `getLimuloToken()` torna `null` ⇒ `prepareHeaders` non mette
 * l'header (giustamente: un `Bearer ` vuoto sarebbe peggio) ⇒ **il server risponde 401, e ha
 * ragione**: quella richiesta non portava credenziali. Il 401 e' vero; e' la CAUSA a essere
 * un'altra, e a quel punto nessuno la sa piu'.
 *
 * ⇒ Chi ha visto la scadenza deve poterlo dire a chi vede il 401. Questa funzione e' quella
 * congiunzione, ed e' pura: prende lo status e l'ultimo esito noto del tentativo di sessione.
 *
 * ⛔ Vale SOLO per `timeout`. Un 401 dopo un rifiuto vero e' un rifiuto vero, e riscriverlo
 * nasconderebbe l'unico caso in cui «rifai l'accesso» e' il consiglio giusto.
 */
export function verdictFor401AfterSessionAttempt(
  status: number | string | undefined,
  lastSessionFailure: LimuloFailure | null,
  /**
   * 🔴 C'e' una sessione utilizzabile adesso? Terzo parametro aggiunto su una domanda di
   * or35 in revisione, e chiude una finestra per COSTRUZIONE invece che con una scadenza.
   *
   * La finestra: scadenza al tempo T (memoria = `timeout`), poi un 401 VERO — sessione
   * davvero revocata — prima di qualunque successo che azzeri la memoria. Li' diremmo
   * «riprova» a chi deve rifare l'accesso, e riprovare non lo sbloccherebbe mai: un LOOP,
   * cioe' peggio del difetto che stiamo togliendo.
   *
   * ⇒ La chiusura non e' un timer: e' che «non abbiamo mandato l'header» spiega un 401
   * **solo se davvero non abbiamo una sessione**. Con una sessione in mano, l'header e'
   * partito e il 401 e' un rifiuto vero — e va detto come tale, perche' li' «rifai
   * l'accesso» e' il consiglio giusto.
   *
   * ⚠️ Misurato quanto la finestra fosse raggiungibile prima di chiuderla: nel percorso
   * servito no (l'unico che crea una sessione dopo un fallimento e'
   * `startLimuloSessionFromCurrentLogto`, che azzera la memoria), ma `runRefresh`
   * (`session.ts:177`) e la diagnostica dev scrivono una sessione **senza** azzerarla. Una
   * finestra raggiungibile solo da un percorso dev e' comunque una finestra, e costava un
   * parametro.
   */
  hasSession: boolean,
): 'timeout' | null {
  if (status !== 401) return null;
  if (hasSession) return null;
  return lastSessionFailure?.verdict === 'timeout' ? 'timeout' : null;
}
